import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'pl' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, replacements?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: { [key in Language]: Record<string, unknown> } = {
  pl: {
    // Landing Page
    Landing: {
      Hero: {
        badge: "✨ Inteligentna platforma do content marketingu",
        title: "Inteligencja AI dla Social Media",
        subtitle: "Wejdź na tę samą falę co Twoja publiczność dzięki generowaniu treści opartemu na AI, analizie konkurencji i wglądom z Instagrama w czasie rzeczywistym.",
        ctaDemo: "Wypróbuj interaktywne demo",
        ctaLearn: "Dowiedz się więcej",
      },
      Features: {
        title: "Inteligentne funkcje",
        subtitle: "Nasza platforma oparta na AI oferuje trzy rewolucyjne możliwości, które zmienią Twoją strategię w social media.",
        aiGeneration: {
          title: "Generowanie treści AI",
          description: "Generuj nieskończone pomysły na treści dopasowane do Twojej branży i odbiorców dzięki naszemu silnikowi AI.",
        },
        competitorAnalysis: {
          title: "Analiza konkurencji",
          description: "Automatycznie śledź i analizuj strategie social media swoich konkurentów, aby wyprzedzać konkurencję.",
        },
        instagramInsights: {
          title: "Wglądy z Instagrama",
          description: "Uzyskaj dane w czasie rzeczywistym o profilach Instagram i trendach, aby zoptymalizować swoją strategię.",
        },
      },
      Demo: {
        title: "Zobacz platformę w akcji",
        subtitle: "Wypróbuj nasze kluczowe funkcje z interaktywnym demo",
        tabContent: "Pomysły na treści",
        tabCompetitors: "Analiza konkurencji",
        tabInstagram: "Wglądy z Instagrama",
        aiGeneratedIdeas: "Pomysły generowane przez AI",
        discoverTrending: "Odkryj trendujące pomysły na treści dopasowane do Twojej branży i odbiorców",
        engagement: "zaangażowanie",
        competitorTracking: "Śledzenie konkurencji",
        monitorCompetitor: "Monitoruj wyniki i strategie konkurentów w czasie rzeczywistym",
        followers: "Obserwujący",
        avgEngagement: "Śr. zaangażowanie",
        topContentType: "Typ najlepszej treści",
        instagramAnalytics: "Analityka profilu Instagram",
        trackInstagram: "Śledź profile Instagram i uzyskuj szczegółowe wglądy w wyniki",
        posts: "Posty",
        lastUpdated: "Ostatnia aktualizacja: 2 godziny temu",
      },
      Pricing: {
        title: "Proste, przejrzyste ceny",
        subtitle: "Wybierz plan odpowiedni dla Twojego biznesu.",
        popular: "Najpopularniejsze",
        perMonth: "/miesiąc",
        starter: {
          name: "Starter",
          description: "Idealny dla małych firm i twórców",
          feature1: "100 pomysłów na treści generowanych przez AI",
          feature2: "Śledzenie 2 profili konkurencji",
          feature3: "Podstawowy panel analityczny",
          feature4: "Wsparcie email",
          cta: "Rozpocznij",
        },
        professional: {
          name: "Professional",
          description: "Dla rozwijających się firm i zespołów marketingowych",
          feature1: "Nieograniczone pomysły na treści AI",
          feature2: "Śledzenie 5 profili konkurencji",
          feature3: "Zaawansowana analityka i wglądy",
          feature4: "Monitorowanie profili Instagram",
          feature5: "Priorytetowe wsparcie",
          feature6: "Eksport i dostęp do API",
          cta: "Rozpocznij",
        },
        enterprise: {
          name: "Enterprise",
          description: "Dla dużych organizacji i agencji",
          feature1: "Wszystko z Professional",
          feature2: "Nieograniczone śledzenie konkurencji",
          feature3: "Opcje white-label",
          feature4: "Niestandardowe integracje",
          feature5: "Dedykowany opiekun konta",
          feature6: "Zaawansowany dostęp do API",
          cta: "Skontaktuj się ze sprzedażą",
        },
      },
      HowItWorks: {
        title: "📈 Jak to działa?",
        subtitle: "Proces, który zmieni Twoje podejście do tworzenia contentu",
        step1: {
          title: "Wybierz branżę",
          description: "Określ swoją niszę i sektor działalności",
        },
        step2: {
          title: "Wklej profile konkurencji",
          description: "Dodaj linki do profili, które chcesz analizować",
        },
        step3: {
          title: "Viraluj – AI wygeneruje pomysły",
          description: "Otrzymuj gotowe scenariusze, które działają",
        },
        benefitsTitle: "Efekty, które zobaczysz od razu",
        benefitsSubtitle: "Sprawdzone rezultaty naszych użytkowników",
        benefit1: "Zredukuj research z 3h do 15 minut",
        benefit2: "Publikuj 3x częściej",
        benefit3: "Zwiększ engagement o 40%",
        stepLabel: "Krok",
      },
      Testimonials: {
        title: "💬 Co mówią użytkownicy",
        subtitle: "Prawdziwe historie sukcesu naszych użytkowników",
        socialProofTitle: "Dołącz do tysięcy zadowolonych użytkowników",
        rating: "⭐ 4.9/5 średnia ocen",
        users: "🚀 10,000+ aktywnych użytkowników",
        growth: "📈 +156% średni wzrost zasięgów",
        description: "Nasi użytkownicy regularnie osiągają lepsze wyniki w social media, oszczędzając przy tym czas i budżet na agencje.",
      },
      CTA: {
        badge: "📥 Zacznij już teraz",
        title: "Zrób swój pierwszy",
        titleHighlight: "viral",
        titleEnd: "już dziś",
        subtitle: "Przekonaj się sam, jak łatwo można tworzyć content, który naprawdę działa i generuje zasięgi!",
        button: "Zrób swój pierwszy viral",
        guarantee1: "Bez karty",
        guarantee2: "Bez ryzyka",
        guarantee3: "Tylko efekt",
        benefitsTitle: "Co otrzymasz:",
        benefit1Value: "50",
        benefit1Text: "Pomysłów na content",
        benefit2Value: "∞",
        benefit2Text: "Analiz konkurencji",
        benefit3Value: "24/7",
        benefit3Text: "Dostęp do platformy",
        footer: "Dołącz do",
        footerCount: "10,000+",
        footerEnd: "twórców, którzy już zwiększają swoje zasięgi z Viraluj",
      },
      Footer: {
        description: "Viraluj.pl to platforma, która pomoże Ci tworzyć content, który rzeczywiście działa i generuje zasięgi.",
        badge: "🚀 Zrób viral już dziś!",
        productTitle: "Produkt",
        product1: "Jak to działa",
        product2: "Cennik",
        product3: "API",
        product4: "Integracje",
        supportTitle: "Wsparcie",
        support1: "Kontakt",
        support2: "FAQ",
        support3: "Blog",
        support4: "Pomoc",
        copyright: "© 2024 Viraluj.pl. Wszystkie prawa zastrzeżone.",
        privacy: "Polityka prywatności",
        terms: "Regulamin",
        cookies: "Cookies",
      },
      Header: {
        dashboard: "Dashboard",
        signOut: "Wyloguj się",
        signIn: "Zaloguj się",
        startFree: "Rozpocznij teraz",
      },
    },
    // Common
    'common.loading': 'Ładowanie...',
    'common.error': 'Błąd',
    'common.save': 'Zapisz',
    'common.cancel': 'Anuluj',
    'common.delete': 'Usuń',
    'common.edit': 'Edytuj',
    'common.close': 'Zamknij',
    'common.confirm': 'Potwierdź',
    'common.week': 'Tydzień',
    'common.month': 'Miesiąc',
    'common.tryAgain': 'Spróbuj ponownie',
    'common.clear': 'Wyczyść',
    'common.seeMore': 'Zobacz więcej',
    'common.add': 'Dodaj',
    'common.remove': 'Usuń',
    'common.backTo': 'Wróć do',
    
    // Navigation
    'nav.dashboard': 'Panel główny',
    'nav.library': 'Biblioteka',
    'nav.favorites': 'Ulubione',
    'nav.ideaGenerator': 'Kreator scenariuszy',
    'nav.inspiration': 'Inspiracje',
    'nav.competitors': 'Konkurencja',
    'nav.analytics': 'Analityka',
    'nav.settings': 'Ustawienia',
    
    // Dashboard
    'dashboard.title': 'Panel główny',
    'dashboard.welcome': 'Witaj z powrotem',
    'dashboard.loadingContent': 'Ładowanie treści...',
    'dashboard.failedToLoad': 'Nie udało się załadować treści',
    'dashboard.weeklyGoal': 'Twój tygodniowy cel 🎯',
    'dashboard.getIdeas': 'Zdobądź pomysły',
    'dashboard.viewAnalytics': 'Zobacz statystyki',
    'dashboard.publicAccount': 'Konto publiczne',
    'dashboard.last7Days': 'ostatnie 7 dni',
    'dashboard.activeTracking': 'Aktywne śledzenie',
    'dashboard.updatedAgo': 'Zaktualizowano około {time} temu',
    'dashboard.followers': 'Obserwujący',
    'dashboard.posts': 'Posty',
    'dashboard.postsPerWeek': 'Tygodniowa średnia',
    'dashboard.goal': 'Cel: {goal}',
    'dashboard.totalIdeas': '{count} pomysłów ogółem',
    'dashboard.featuredToday': '{count} wyróżnionych dzisiaj',
    'dashboard.viewAllIdeas': 'Zobacz wszystkie pomysły',
    'dashboard.noFeaturedIdeas': 'Brak wyróżnionych pomysłów',
    'dashboard.noFeaturedIdeasDesc': 'Wygeneruj więcej treści lub sprawdź bibliotekę',
    'dashboard.noContentYet': 'Brak pomysłów na treści',
    'dashboard.completeSetup': 'Zakończ konfigurację',
    'dashboard.firstBatchGenerating': 'Pierwsza paczka jest generowana! Zwykle trwa to 2-3 minuty po zakończeniu onboardingu',
    'dashboard.addInspirationSources': 'Dodaj źródła inspiracji, aby rozpocząć generowanie pomysłów na treści',
    'dashboard.checkAgain': 'Sprawdź ponownie',
    'dashboard.addMoreSources': 'Dodaj więcej źródeł',
    'dashboard.getStarted': 'Zacznij',
    'dashboard.topContentIdeas': 'Top 3 pomysły na treści',
    'dashboard.highestViralScore': 'Najwyższy Wynik Viralowy',
    'dashboard.used': 'Użyto',
    
    // Competitors
    'competitors.title': 'Konkurencja',
    'competitors.description': 'Śledź i analizuj wyniki konkurencji',
    'competitors.tracked': 'konkurentów śledzonych',
    'competitors.competitor': 'konkurent',
    'competitors.competitors': 'konkurenci',
    'competitors.add': 'Dodaj',
    'competitors.addCompetitor': 'Dodaj konkurenta',
    'competitors.enterUsername': 'Wprowadź nazwę użytkownika Instagram lub URL',
    'competitors.manageCompetitors': 'Zarządzaj konkurentami',
    'competitors.addNew': 'Dodaj nowych konkurentów lub zarządzaj istniejącymi',
    'competitors.noCompetitors': 'Nie dodano jeszcze konkurentów',
    'competitors.noCompetitorsDesc': 'Zacznij od dodania nazw użytkowników Instagram kont, które chcesz śledzić i porównywać ze swoimi wynikami',
    'competitors.removeTitle': 'Usuń konkurenta',
    'competitors.removeDesc': 'Czy na pewno chcesz usunąć @{username} z listy konkurentów? Ta akcja jest nieodwracalna.',
    'competitors.remove': 'Usuń',
    'competitors.overview': 'Przegląd',
    'competitors.growth': 'Wzrost',
    'competitors.manage': 'Zarządzaj',
    'competitors.performanceOverview': 'Przegląd wyników',
    'competitors.growthComparison': 'Porównanie wzrostu',
    'competitors.weeklyStats': 'Statystyki tygodniowe',
    'competitors.followers': 'Obserwujący',
    'competitors.following': 'Obserwowani',
    'competitors.posts': 'Posty',
    'competitors.performanceComparison': 'Porównanie wyników',
    'competitors.comparePerformance': 'Porównaj swoje wyniki ze wszystkimi konkurentami',
    'competitors.youVsBest': 'Ty vs najlepsi konkurenci w ostatnich 7 dniach',
    'competitors.showMore': 'Pokaż więcej konkurentów',
    'competitors.followerGrowth': 'Wzrost obserwujących w czasie - znormalizowany dla lepszego porównania',
    'competitors.postsPerWeek': 'Posty/tydzień',
    'competitors.collectingData': 'Zbieranie danych...',
    'competitors.fullMetrics': 'Pełne metryki za {days} dni',
    'competitors.you': 'Ty',
    'competitors.chartNotReady': 'Wykres będzie dostępny po zebraniu co najmniej 2 dni danych. Sprawdź ponownie wkrótce.',
    'competitors.dataCollectionInfo': 'Zbieramy dane z profili Instagram. Pełne metryki będą dostępne za kilka dni.',
    'competitors.trackPerformance': 'Śledź wyniki konkurencji',
    'competitors.dataCollectionInProgress': 'Trwa zbieranie danych',
    
    // First Content Celebration
    'firstContent.title': 'Twoje pierwsze pomysły na treści są gotowe!',
    'firstContent.description': 'Wygenerowaliśmy {{count}} pomysłów z Twoich źródeł inspiracji.',
    'firstContent.pipelineActive': 'Twój pipeline treści jest teraz aktywny! Nowe pomysły będą generowane automatycznie.',
    'firstContent.viewButton': 'Zobacz Moje Pomysły',
    
    // Library
    'library.mainTitle': 'Biblioteka treści',
    'library.mainDescription': 'Przeglądaj i organizuj pomysły na treści ze źródeł inspiracji',
    'library.topIdeas': 'Top 3 pomysły',
    'library.favorites': 'Ulubione',
    'library.noFavorites': 'Brak ulubionych pomysłów',
    'library.noFavoritesHint': 'Kliknij ikonę serca przy pomyśle, aby dodać do ulubionych',
    'library.allIdeas': 'Wszystkie pomysły',
    'library.basedOn': 'Oparte na:',
    'library.viewIdea': 'Zobacz pomysł',
    'library.loading': 'Ładowanie biblioteki...',
    'library.failedToLoad': 'Nie udało się załadować treści',
    'library.tryAgain': 'Spróbuj ponownie',
    'library.noContent': 'Brak treści ze źródeł inspiracji',
    'library.noContentDesc': 'Pomysły na treści wygenerowane ze źródeł inspiracji pojawią się tutaj',
    'library.goToDashboard': 'Przejdź do pulpitu',
    'library.filters': 'Filtry:',
    'library.source': 'Źródło:',
    'library.allSources': 'Wszystkie źródła',
    'library.score': 'Wynik:',
    'library.allScores': 'Wszystkie wyniki',
    'library.highScore': 'Wysoki (8+)',
    'library.mediumScore': 'Średni (5-7)',
    'library.lowScore': 'Niski (0-4)',
    'library.date': 'Data:',
    'library.newestFirst': 'Najnowsze pierwsze',
    'library.oldestFirst': 'Najstarsze pierwsze',
    'library.noMatches': 'Brak treści pasujących do filtrów',
    'library.noMatchesDesc': 'Spróbuj dostosować kryteria filtrowania',
    'library.clearAllFilters': 'Wyczyść wszystkie filtry',
    'library.totalIdeas': 'Wszystkie pomysły',
    'library.avgScore': 'Średni wynik',
    'library.itemsPerPage': 'Na stronę',
    
    // Favorites
    'favorites.title': 'Ulubione treści',
    'favorites.description': 'Twoje zapisane pomysły na treści',
    'favorites.backToLibrary': 'Wróć do biblioteki',
    'favorites.favoriteContent': 'Ulubione treści',
    'favorites.savedContent': 'Twoje zapisane pomysły na treści dla szybkiego dostępu',
    'favorites.filters': 'Filtry:',
    'favorites.source': 'Źródło:',
    'favorites.allSources': 'Wszystkie źródła',
    'favorites.score': 'Wynik:',
    'favorites.allScores': 'Wszystkie wyniki',
    'favorites.date': 'Data:',
    'favorites.newestFirst': 'Najnowsze',
    'favorites.oldestFirst': 'Najstarsze',
    'favorites.showing': 'Pokazuję {filtered} z {total} ulubionych pomysłów',
    'favorites.noFavorites': 'Brak ulubionych treści',
    'favorites.noFavoritesDesc': 'Kliknij ikonę serca na dowolnym pomyśle, aby dodać go do ulubionych',
    'favorites.browseContent': 'Przeglądaj treści',
    'favorites.noMatch': 'Brak ulubionych pasujących do filtrów',
    'favorites.noMatchDesc': 'Spróbuj dostosować kryteria filtrowania, aby zobaczyć więcej wyników',
    'favorites.clearAllFilters': 'Wyczyść wszystkie filtry',
    'favorites.favoriteIdeas': 'Ulubione pomysły',
    'favorites.avgScore': 'Średni wynik',
    'favorites.loadingFavorites': 'Ładowanie ulubionych...',
    'favorites.failedToLoad': 'Nie udało się załadować treści',
    'favorites.highScore': 'Wysoki (8+)',
    'favorites.mediumScore': 'Średni (5-7)',
    'favorites.lowScore': 'Niski (0-4)',
    // Favorites Tabs
    'favorites.tabs.all': 'Wszystkie',
    'favorites.tabs.inspiration': 'Inspiracje',
    'favorites.tabs.generated': 'Autorskie',
    
    // Inspiration
    'inspiration.title': 'Źródła inspiracji',
    'inspiration.description': 'Dodaj do 5 profili do scrapowania w celu inspiracji treściami',
    'inspiration.sources': '{current}/{max} źródeł',
    'inspiration.nextScraping': 'Następna analiza danych',
    'inspiration.weeklyRefresh': 'Cotygodniowe odświeżanie treści',
    'inspiration.scrapingSchedule': 'Scrapowanie odbywa się co poniedziałek o 9:00',
    'inspiration.addSource': 'Znajdź inspiracje z całego świata. Dodaj źródło inspiracji:',
    'inspiration.addSourceDesc': 'Dodaj profil na Instagramie, z którego chcesz się uczyć.',
    'inspiration.enterUrl': 'Wprowadź URL Instagram, TikTok lub YouTube',
    'inspiration.enterInstagramUrl': 'Wprowadź nazwę użytkownika lub URL Instagram',
    'inspiration.addButton': 'Dodaj źródło',
    'inspiration.maxReached': 'Osiągnięto maksymalną liczbę źródeł inspiracji.',
    'inspiration.postsScraped': 'Sprawdzono postów:',
    'inspiration.posts': 'Posty',
    'inspiration.lastScraped': 'Ostatnio zescrapowane',
    'inspiration.never': 'Nigdy',
    'inspiration.noSources': 'Nie dodano jeszcze źródeł inspiracji',
    'inspiration.noSourcesDesc': 'Zacznij od dodania profili mediów społecznościowych, które chcesz scrapować w celu inspiracji treściami',
    'inspiration.removeSource': 'Usuń źródło inspiracji',
    'inspiration.removeSourceDesc': 'Czy na pewno chcesz usunąć @{username}? Zatrzyma to przyszłe scrapowanie z tego źródła, ale istniejące treści zostaną zachowane w bibliotece.',
    'inspiration.scrapingContent': 'Scrapowanie treści...',
    'inspiration.completedCount': '{completed}/{total} ukończono',
    'inspiration.processing': 'Przetwarzanie @{username}...',
    'inspiration.adding': 'Dodawanie...',
    'inspiration.addSourceButton': 'Dodaj źródło',
    'inspiration.maxSourcesReached': 'Osiągnięto maksymalną liczbę źródeł inspiracji.',
    'inspiration.loadingSources': 'Ładowanie źródeł inspiracji...',
    'inspiration.invalidUrl': 'Nieprawidłowy URL',
    'inspiration.invalidUrlDesc': 'Wprowadź prawidłowy URL Instagram, TikTok lub YouTube',
    'inspiration.sourceAdded': 'Źródło dodane!',
    'inspiration.sourceAddedDesc': 'Analiza treści rozpocznie się wkrótce. Sprawdź ponownie za kilka minut.',
    'inspiration.failedToAdd': 'Nie udało się dodać źródła',
    'inspiration.sourceRemoved': 'Źródło usunięte',
    'inspiration.sourceRemovedDesc': '@{username} usunięte. Istniejące treści z tego źródła zostały zachowane.',
    'inspiration.failedToRemove': 'Nie udało się usunąć źródła',
    
    // Analytics
    'analytics.title': 'Analityka',
    'analytics.description': 'Śledź wzrost Instagram i wyniki treści',
    'analytics.followers': 'Obserwujący',
    'analytics.following': 'Obserwowani',
    'analytics.posts': 'Posty',
    'analytics.thisWeek': 'w tym tygodniu',
    'analytics.thisMonth': 'w tym miesiącu',
    'analytics.weeklyGrowth': 'Wzrost tygodniowy',
    'analytics.followingActivity': 'Aktywność obserwowanych',
    'analytics.contentActivity': 'Aktywność treści',
    'analytics.monthlyGrowth': 'Wzrost miesięczny',
    'analytics.weeklyPerformance': 'Wyniki tygodniowe',
    'analytics.monthlyPerformance': 'Wyniki miesięczne',
    'analytics.last7Days': 'Trend ostatnich 7 dni',
    'analytics.last30Days': 'Trend ostatnich 30 dni',
    'analytics.weeklyGoal': 'Cel tygodniowy 🎯',
    'analytics.monthlyGoal': 'Cel miesięczny 🎯',
    'analytics.getIdeas': 'Zdobądź pomysły',
    'analytics.overview': 'Przegląd',
    'analytics.growthAnalysis': 'Analiza wzrostu',
    'analytics.contentPerformance': 'Wyniki treści',
    'analytics.noData': 'Brak dostępnych danych analitycznych',
    'analytics.noDataDesc': 'Połącz swój profil Instagram i poczekaj na rozpoczęcie zbierania danych.',
    'analytics.dataCollection': 'Trwa zbieranie danych',
    'analytics.chartTitle': 'Przegląd wzrostu',
    'analytics.chartDescription': 'Zielone słupki pokazują wzrost obserwujących, czerwone straty w wybranym okresie',
    'analytics.lastWeek': 'Ostatnie 7 dni',
    'analytics.lastMonth': 'Ostatnie 30 dni',
    'analytics.dailyChange': 'Dzienna zmiana',
    'analytics.followerChange': 'Zmiana obserwujących',
    'analytics.followerGains': 'Wzrost obserwujących',
    'analytics.followerLosses': 'Spadek obserwujących',
    'analytics.bestGrowthDay': 'Najlepszy dzień wzrostu',
    'analytics.totalWeeklyChange': 'Całkowita zmiana tygodniowa',
    'analytics.totalMonthlyChange': 'Całkowita zmiana miesięczna',
    'analytics.growthDays': 'Dni wzrostu',
    'analytics.followersGained': '+{count} zdobytych obserwujących',
    'analytics.netFollowerChange': 'Zmiana netto obserwujących w tym tygodniu',
    'analytics.netFollowerChangeMonth': 'Zmiana netto obserwujących w tym miesiącu',
    'analytics.outOfDays': 'Z {count} śledzonych dni',
    'analytics.collecting': 'Zbieranie...',
    'analytics.needMoreData': 'Potrzeba więcej danych do analizy',
    'analytics.noGainsThisWeek': 'Brak wzrostu w tym tygodniu',
    'analytics.noGainsThisMonth': 'Brak wzrostu w tym miesiącu',
    'analytics.contentIdeas': 'Pomysły na treści',
    'analytics.inspirationSources': 'Źródła inspiracji',
    'analytics.viralScore': 'Wynik viralowy',
    'analytics.generatedIdeas': 'Wygenerowane pomysły',
    'analytics.activeSources': 'Aktywne źródła',
    'analytics.averageQuality': 'Średnia jakość',
    'analytics.aiPowered': 'Napędzane AI',
    'analytics.trending': 'Trenujące',
    'analytics.highQuality': 'Wysoka jakość',
    'analytics.mediumQuality': 'Średnia jakość',
    'analytics.lowQuality': 'Niska jakość',
    'analytics.totalIdeas': 'Pomysły na treści',
    'analytics.aiGeneratedContent': 'Wygenerowane przez AI',
    'analytics.averageContentScore': 'Średni wynik jakości',
    'analytics.basedOnAIIdeas': 'Na podstawie Twoich pomysłów',
    'analytics.scoreInterpretation': 'Jak interpretować wynik',
    'analytics.understandYourScore': 'Zrozum jakość swoich źródeł inspiracji',
    'analytics.excellentContent': 'Doskonałe źródła inspiracji',
    'analytics.goodContent': 'Dobre źródła inspiracji',
    'analytics.needsImprovement': 'Do poprawy',
    'analytics.noContentYet': 'Brak treści',
    'analytics.startGenerating': 'Zacznij generować pomysły na treści używając naszego AI',
    'analytics.generateFirstIdea': 'Wygeneruj pierwszy pomysł',
    
    // Idea Generator
    'ideaGenerator.title': 'Kreator scenariuszy',
    'ideaGenerator.description': 'Wygeneruj scenariusze do social media na wybrany temat',
    'ideaGenerator.topicLabel': 'Wpisz temat, na który chcesz przygotować scenariusz:',
    'ideaGenerator.topicPlaceholder': 'Wpisz temat, na który chcesz wygenerować scenariusz do social media...',
    'ideaGenerator.topicHint': 'Czym bardziej szczegółowy temat, tym lepsze wyniki. Określ konkretny problem swojej grupy docelowej - zamiast "zdrowe odżywianie" napisz "jak jeść zdrowo pracując 10h dziennie"',
    'ideaGenerator.generateButton': 'Wygeneruj pomysły',
    'ideaGenerator.generating': 'Generowanie...',
    
    // Settings
    'settings.title': 'Ustawienia',
    'settings.description': 'Zarządzaj ustawieniami konta i preferencjami',
    'settings.industrySettings': 'Ustawienia branży',
    'settings.updateIndustry': 'Zaktualizuj swoją branżę, aby otrzymywać bardziej trafne sugestie treści',
    'settings.industryCategory': 'Kategoria branży',
    'settings.selectYourIndustry': 'Wybierz swoją branżę i doprecyzuj kategorię',
    'settings.saveChanges': 'Zapisz zmiany',
    'settings.selectMainCategory': 'Wybierz kategorię główną',
    'settings.selectSubcategory': 'Doprecyzuj branżę',
    'settings.currentIndustry': 'Obecna branża',
    'settings.accountInfo': 'Informacje o koncie',
    'settings.viewAccountDetails': 'Zobacz szczegóły swojego konta',
    'settings.securitySettings': 'Ustawienia bezpieczeństwa',
    'settings.emailAddress': 'Adres e-mail',
    'settings.instagramUsername': 'Nazwa użytkownika Instagram',
    'settings.changeEmail': 'Zmień adres e-mail',
    'settings.changeEmailDesc': 'Zaktualizuj swój adres e-mail. Będziesz musiał potwierdzić nowy e-mail.',
    'settings.newEmail': 'Nowy adres e-mail',
    'settings.updateEmail': 'Zaktualizuj e-mail',
    'settings.changePassword': 'Zmień hasło',
    'settings.changePasswordDesc': 'Zaktualizuj hasło do konta. Będziesz musiał wprowadzić aktualne hasło.',
    'settings.currentPassword': 'Aktualne hasło',
    'settings.newPassword': 'Nowe hasło',
    'settings.confirmPassword': 'Potwierdź nowe hasło',
    'settings.updatePassword': 'Zaktualizuj hasło',
    'settings.saving': 'Zapisywanie...',
    'settings.updating': 'Aktualizowanie...',
    'settings.notAuthenticated': 'Użytkownik nie jest uwierzytelniony',
    'settings.selectIndustry': 'Wybierz branżę',
    'settings.industryUpdated': 'Branża zaktualizowana',
    'settings.industryUpdatedDesc': 'Twoja branża została pomyślnie zaktualizowana.',
    'settings.errorUpdatingIndustry': 'Błąd aktualizacji branży',
    'settings.industryNotSet': 'Branża nie została jeszcze wybrana',
    'settings.editIndustry': 'Edytuj branżę',
    'settings.legacyCategoryNotice': 'Odświeżyliśmy listę kategorii. Wybierz na nowo, by Viraluj generował lepiej dopasowany content.',
    'settings.enterNewEmail': 'Wprowadź nowy adres e-mail',
    'settings.emailUpdateInitiated': 'Rozpoczęto aktualizację e-maila',
    'settings.emailUpdateInitiatedDesc': 'Sprawdź swój nowy adres e-mail w celu potwierdzenia zmiany.',
    'settings.errorUpdatingEmail': 'Błąd aktualizacji e-maila',
    'settings.fillPasswordFields': 'Wypełnij wszystkie pola hasła',
    'settings.passwordsDoNotMatch': 'Nowe hasła nie pasują do siebie',
    'settings.passwordTooShort': 'Nowe hasło musi mieć co najmniej 6 znaków',
    'settings.currentPasswordIncorrect': 'Obecne hasło jest nieprawidłowe',
    'settings.passwordUpdated': 'Hasło zaktualizowane',
    'settings.passwordUpdatedDesc': 'Twoje hasło zostało pomyślnie zaktualizowane.',
    'settings.errorUpdatingPassword': 'Błąd aktualizacji hasła',
    'settings.selectIndustryPlaceholder': 'Wybierz swoją branżę',
    'settings.notSelected': 'Nie wybrano',
    'settings.notProvided': 'Nie podano',
    'settings.industries.fashion': 'Moda i lifestyle',
    'settings.industries.food': 'Gastronomia',
    'settings.industries.business': 'Biznes i finanse',
    'settings.industries.gaming': 'Gaming i tech',
    'settings.industries.automotive': 'Motoryzacja',
    'settings.industries.retail': 'Handel detaliczny',
    'settings.industries.realestate': 'Nieruchomości',
    'settings.industries.other': 'Inne',
    'settings.instagram.title': 'Profil Instagram',
    'settings.instagram.description': 'Połącz swój profil Instagram',
    'settings.instagram.usernameLabel': 'Nazwa użytkownika Instagram',
    'settings.instagram.usernamePlaceholder': 'twoja_nazwa_uzytkownika',
    'settings.instagram.locked': 'To pole jest zablokowane dla integralności danych.',
    
    // Welcome Dialog
    'welcome.title': 'Witaj! 🎉',
    'welcome.titleWithName': 'Witaj, {{name}}! 🎉',
    'welcome.subtitle': 'Poznaj swoją nową platformę do tworzenia viralowych treści na Instagram',
    'welcome.feature1Title': '1. Analizuj konkurencję',
    'welcome.feature1Desc': 'Śledzimy Twoich konkurentów i analizujemy ich najbardziej skuteczne treści, abyś wiedział, co działa w Twojej niszy.',
    'welcome.feature2Title': '2. Generuj pomysły AI',
    'welcome.feature2Desc': 'Nasza AI tworzy spersonalizowane pomysły na treści bazując na viralowych wzorcach i Twojej branży.',
    'welcome.feature3Title': '3. Publikuj i rozwijaj się',
    'welcome.feature3Desc': 'Używaj gotowych skryptów, śledź swój wzrost i wyprzedzaj konkurencję dzięki insights opartym na danych.',
    'welcome.tipTitle': '💡 Szybki start',
    'welcome.tipDesc': 'Rozpocznij od dodania źródeł inspiracji w zakładce Inspiracje, a my automatycznie wygenerujemy Twoje pierwsze pomysły na treści!',
    'welcome.exploreDashboard': 'Eksploruj Panel',
    'welcome.getStarted': 'Dodaj Źródła Inspiracji',
    'settings.instagram.usernameHint': 'Wprowadź nazwę użytkownika lub wklej link do profilu Instagram',
    'settings.instagram.saveButton': 'Zapisz nazwę użytkownika Instagram',
    'settings.instagram.notAuthenticated': 'Musisz być zalogowany, aby zapisać nazwę użytkownika Instagram.',
    'settings.instagram.enterUsername': 'Proszę wprowadzić nazwę użytkownika Instagram.',
    'settings.instagram.cannotChange': 'Nie można zmienić nazwy użytkownika',
    'settings.instagram.cannotChangeDesc': 'Twoja nazwa użytkownika Instagram jest już ustawiona i nie może być zmieniona. Skontaktuj się z pomocą techniczną, jeśli musisz ją zaktualizować.',
    'settings.instagram.saved': 'Nazwa użytkownika Instagram zapisana',
    'settings.instagram.savedDesc': 'Twoja nazwa użytkownika Instagram została pomyślnie zapisana.',
    'settings.enterNewEmailPlaceholder': 'Wprowadź nowy adres e-mail',
    'settings.enterCurrentPasswordPlaceholder': 'Wprowadź obecne hasło',
    'settings.enterNewPasswordPlaceholder': 'Wprowadź nowe hasło',
    'settings.confirmPasswordPlaceholder': 'Potwierdź nowe hasło',
    
    // Product Tour
    'tour.welcome.title': 'Witaj w Viraluj.pl!',
    'tour.welcome.description': 'Poznaj kluczowe funkcje platformy w 1 minutę. Przeprowadzimy Cię krok po kroku przez wszystkie możliwości.',
    'tour.profileStats.title': '📊 Twoje Statystyki',
    'tour.profileStats.description': 'Śledź wzrost Twojego profilu Instagram - obserwujących, posty i cele tygodniowe.',
    'tour.competitors.title': '🎯 Analiza Konkurencji',
    'tour.competitors.description': 'Zobacz, co działa u Twoich konkurentów. Analizujemy ich najlepsze treści, abyś mógł być o krok przed nimi.',
    'tour.competitors.navTitle': '🎯 Konkurencja',
    'tour.competitors.navDescription': 'Szczegółowe porównania wydajności i strategie konkurencji. Wszystko w jednym miejscu.',
    'tour.featuredIdeas.title': '💡 Top 3 Pomysły',
    'tour.featuredIdeas.description': 'Najlepsze pomysły wybrane na podstawie potencjału wiralowego. Kliknij, aby zobaczyć pełne detale!',
    'tour.library.title': '📚 Biblioteka Pomysłów',
    'tour.library.description': 'Wszystkie wygenerowane pomysły na treści w jednym miejscu. Twoja osobista skarbnica inspiracji!',
    'library.seeMore': 'Zobacz więcej',
    'library.pagination.previous': 'Poprzednia',
    'library.pagination.next': 'Następna',
    'library.pagination.pageInfo': 'Strona {{current}} z {{total}}',
    'tour.favorites.title': '❤️ Ulubione',
    'tour.favorites.description': 'Kliknij ikonę serca, aby zapisać najlepsze pomysły. Łatwo do nich wrócisz!',
    'tour.favoritesPage.title': '⭐ Twoje Ulubione',
    'tour.favoritesPage.description': 'Zapisane pomysły i możliwość oznaczenia jako "użyte" po publikacji. Organizuj swoją pracę!',
    'tour.inspiration.title': '✨ Źródła Inspiracji',
    'tour.inspiration.description': 'Dodaj profile Instagram, które Cię inspirują. Automatycznie analizujemy ich najlepsze treści.',
    'tour.analytics.title': '📊 Analityka',
    'tour.analytics.description': 'Śledź postępy i skuteczność treści. Zobacz dokładnie, co działa w Twojej strategii!',
    'tour.scenarioCreator.title': '✍️ Kreator Scenariuszy',
    'tour.scenarioCreator.description': 'Stwórz swój pierwszy scenariusz! Wpisz temat, a my wygenerujemy gotowy scenariusz do nagrania.',
    'tour.scenarioCreator.cta': '👉 Kliknij "Zakończ", aby przejść do kreatora i stworzyć pierwszy scenariusz!',
    'tour.buttons.back': 'Wstecz',
    'tour.buttons.close': 'Zamknij',
    'tour.buttons.finish': 'Stwórz scenariusz →',
    'tour.buttons.next': 'Dalej',
    'tour.buttons.skip': 'Pomiń',
    
    // Settings Product Tour
    'settings.productTour.title': 'Przewodnik po Platformie',
    'settings.productTour.description': 'Uruchom ponownie interaktywny przewodnik, który pokaże Ci kluczowe funkcje platformy.',
    'settings.productTour.restart': 'Uruchom Przewodnik',
    
    // Days of week
    'day.mon': 'Pon',
    'day.tue': 'Wt',
    'day.wed': 'Śr',
    'day.thu': 'Czw',
    'day.fri': 'Pt',
    'day.sat': 'Sob',
    'day.sun': 'Ndz',
    
    // Library
    'library.title': 'Biblioteka Treści',
    'library.description': 'Przeglądaj i organizuj pomysły na treści ze źródeł inspiracji',
    'library.view': 'Widok',
    'library.viewCork': 'Tablica',
    'library.viewList': 'Lista',
    'library.tabs.all': 'Inspiracje',
    'library.tabs.best': 'Najlepsze',
    'library.tabs.used': 'Użyte',
    'library.tabs.generated': 'Autorskie',
    'library.tabs.trash': 'Kosz',
    
    // Header
    'header.notifications': 'Powiadomienia',
    'header.markAllRead': 'Oznacz wszystkie jako przeczytane',
    'header.noNotifications': 'Brak powiadomień',
    'header.settings': 'Ustawienia',
    'header.logout': 'Wyloguj',
    
    // Content Pipeline
    'contentPipeline.title': 'Proces Treści',
    'contentPipeline.lastUpdated': 'Ostatnia aktualizacja: {time}',
    'contentPipeline.sources': 'Źródła:',
    'contentPipeline.of': 'z',
    'contentPipeline.used': 'użyto',
    'contentPipeline.addMoreSources': 'Dodaj Więcej Źródeł',
    'contentPipeline.limitReached': 'Osiągnięto Limit',
    'contentPipeline.addInstagramSource': 'Dodaj Źródło Instagram',
    'contentPipeline.limitReachedDesc': 'Osiągnąłeś limit 5 źródeł. Usuń źródło, aby dodać nowe.',
    'contentPipeline.slotsRemaining': '{count} wolnych miejsc',
    'contentPipeline.instagramProfileUrl': 'URL Profilu Instagram',
    'contentPipeline.usernamePlaceholder': '@nazwa_uzytkownika lub https://instagram.com/nazwa_uzytkownika',
    'contentPipeline.adding': 'Dodawanie...',
    'contentPipeline.addSource': 'Dodaj Źródło',
    'contentPipeline.yourInspirationSources': 'Twoje Źródła Inspiracji ({current} z {max})',
    'contentPipeline.limitReachedBadge': 'Osiągnięto limit',
    'contentPipeline.removeSourceTip': '💡 Usuń źródło, aby dodać nowe',
    'contentPipeline.generatingContent': '🔄 Generowanie Treści',
    'contentPipeline.analyzingSources': 'Analizujemy Twoje {count} źródła inspiracji...',
    'contentPipeline.usuallyTakes': 'Zwykle trwa to 2-3 minuty.',
    'contentPipeline.loadingPipeline': 'Ładowanie statusu procesu...',
    'contentPipeline.justNow': 'Właśnie teraz',
    'contentPipeline.minutesAgo': '{count} minut temu',
    'contentPipeline.hoursAgo': '{count} godzin temu',
    'contentPipeline.daysAgo': '{count} dni temu',
    'contentPipeline.pleaseEnterUrl': 'Wprowadź URL Instagram',
    
    // Onboarding Generation Status
    'onboardingGeneration.title': '🔄 Generowanie Twoich Pierwszych Pomysłów na Treści',
    'onboardingGeneration.analyzingSources': 'Analizujemy Twoje {count} źródła inspiracji...',
    'onboardingGeneration.usuallyTakes': 'Zwykle trwa to 2-3 minuty.',
    'onboardingGeneration.analyzingPatterns': 'Analizowanie wzorców viralowych treści...',
    'onboardingGeneration.autoRefresh': '💡 Automatycznie odświeżamy co 30 sekund, aby sprawdzić nowe treści',
  },
  en: {
    // Landing Page
    Landing: {
      Hero: {
        badge: "✨ Intelligent Content Marketing Platform",
        title: "AI-Powered Social Media Intelligence",
        subtitle: "Get on the same wavelength as your audience with AI-powered content generation, competitor analysis, and real-time Instagram insights.",
        ctaDemo: "Try Interactive Demo",
        ctaLearn: "Learn More",
      },
      Features: {
        title: "Intelligent Features",
        subtitle: "Our AI-powered platform offers three revolutionary capabilities to transform your social media strategy.",
        aiGeneration: {
          title: "AI Content Generation",
          description: "Generate endless content ideas tailored to your industry and audience with our AI-powered engine.",
        },
        competitorAnalysis: {
          title: "Competitor Analysis",
          description: "Automatically track and analyze your competitors' social media strategies to stay ahead of the curve.",
        },
        instagramInsights: {
          title: "Instagram Insights",
          description: "Get real-time data on Instagram profiles and trending content to optimize your strategy.",
        },
      },
      Demo: {
        title: "See the Platform in Action",
        subtitle: "Try our key features with interactive demo",
        tabContent: "Content Ideas",
        tabCompetitors: "Competitor Analysis",
        tabInstagram: "Instagram Insights",
        aiGeneratedIdeas: "AI-Generated Content Ideas",
        discoverTrending: "Discover trending content ideas tailored to your industry and audience",
        engagement: "engagement",
        competitorTracking: "Competitor Tracking",
        monitorCompetitor: "Monitor your competitors' performance and strategies in real-time",
        followers: "Followers",
        avgEngagement: "Avg. Engagement",
        topContentType: "Top Content Type",
        instagramAnalytics: "Instagram Profile Analytics",
        trackInstagram: "Track Instagram profiles and get detailed insights on performance",
        posts: "Posts",
        lastUpdated: "Last updated: 2 hours ago",
      },
      Pricing: {
        title: "Simple, Transparent Pricing",
        subtitle: "Choose the plan that's right for your business.",
        popular: "Most Popular",
        perMonth: "/month",
        starter: {
          name: "Starter",
          description: "Perfect for small businesses and creators",
          feature1: "100 AI-generated content ideas",
          feature2: "2 competitor profiles tracking",
          feature3: "Basic analytics dashboard",
          feature4: "Email support",
          cta: "Get Started",
        },
        professional: {
          name: "Professional",
          description: "For growing businesses and marketing teams",
          feature1: "Unlimited AI content ideas",
          feature2: "5 competitor profiles tracking",
          feature3: "Advanced analytics & insights",
          feature4: "Instagram profile monitoring",
          feature5: "Priority support",
          feature6: "Export & API access",
          cta: "Get Started",
        },
        enterprise: {
          name: "Enterprise",
          description: "For large organizations and agencies",
          feature1: "Everything in Professional",
          feature2: "Unlimited competitor tracking",
          feature3: "White-label options",
          feature4: "Custom integrations",
          feature5: "Dedicated account manager",
          feature6: "Advanced API access",
          cta: "Contact Sales",
        },
      },
      HowItWorks: {
        title: "📈 How It Works?",
        subtitle: "The process that will transform your approach to content creation",
        step1: {
          title: "Choose Industry",
          description: "Define your niche and business sector",
        },
        step2: {
          title: "Add Competitor Profiles",
          description: "Add links to profiles you want to analyze",
        },
        step3: {
          title: "Viraluj – AI Generates Ideas",
          description: "Get ready-made scenarios that work",
        },
        benefitsTitle: "Results You'll See Immediately",
        benefitsSubtitle: "Proven results from our users",
        benefit1: "Reduce research from 3h to 15 minutes",
        benefit2: "Publish 3x more often",
        benefit3: "Increase engagement by 40%",
        stepLabel: "Step",
      },
      Testimonials: {
        title: "💬 What Users Say",
        subtitle: "Real success stories from our users",
        socialProofTitle: "Join thousands of satisfied users",
        rating: "⭐ 4.9/5 average rating",
        users: "🚀 10,000+ active users",
        growth: "📈 +156% average reach growth",
        description: "Our users regularly achieve better social media results while saving time and agency budgets.",
      },
      CTA: {
        badge: "📥 Start Now",
        title: "Create your first",
        titleHighlight: "viral",
        titleEnd: "today",
        subtitle: "See for yourself how easy it is to create content that really works and generates reach!",
        button: "Create your first viral",
        guarantee1: "No card required",
        guarantee2: "No risk",
        guarantee3: "Only results",
        benefitsTitle: "What you get:",
        benefit1Value: "50",
        benefit1Text: "Content Ideas",
        benefit2Value: "∞",
        benefit2Text: "Competitor Analyses",
        benefit3Value: "24/7",
        benefit3Text: "Platform Access",
        footer: "Join",
        footerCount: "10,000+",
        footerEnd: "creators who are already growing their reach with Viraluj",
      },
      Footer: {
        description: "Viraluj.pl is a platform that will help you create content that actually works and generates reach.",
        badge: "🚀 Go viral today!",
        productTitle: "Product",
        product1: "How it works",
        product2: "Pricing",
        product3: "API",
        product4: "Integrations",
        supportTitle: "Support",
        support1: "Contact",
        support2: "FAQ",
        support3: "Blog",
        support4: "Help",
        copyright: "© 2024 Viraluj.pl. All rights reserved.",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        cookies: "Cookies",
      },
      Header: {
        dashboard: "Dashboard",
        signOut: "Sign Out",
        signIn: "Sign In",
        startFree: "Start Now",
      },
    },
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.week': 'Week',
    'common.month': 'Month',
    'common.tryAgain': 'Try Again',
    'common.clear': 'Clear',
    'common.seeMore': 'See More',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.backTo': 'Back to',
    
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.library': 'Library',
    'nav.favorites': 'Favorites',
    'nav.ideaGenerator': 'Scenario Generator',
    'nav.inspiration': 'Inspiration',
    'nav.competitors': 'Competitors',
    'nav.analytics': 'Analytics',
    'nav.settings': 'Settings',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',
    'dashboard.loadingContent': 'Loading your content...',
    'dashboard.failedToLoad': 'Failed to load content',
    'dashboard.weeklyGoal': 'Your weekly goal 🎯',
    'dashboard.getIdeas': 'Get Ideas',
    'dashboard.viewAnalytics': 'View Analytics',
    'dashboard.publicAccount': 'Public Account',
    'dashboard.last7Days': 'last 7 days',
    'dashboard.activeTracking': 'Active tracking',
    'dashboard.updatedAgo': 'Updated about {time} ago',
    'dashboard.followers': 'Followers',
    'dashboard.posts': 'Posts',
    'dashboard.postsPerWeek': 'Posts/week',
    'dashboard.goal': 'Goal: {goal}',
    'dashboard.totalIdeas': '{count} total ideas',
    'dashboard.featuredToday': '{count} featured today',
    'dashboard.viewAllIdeas': 'View All Ideas',
    'dashboard.noFeaturedIdeas': 'No featured ideas available',
    'dashboard.noFeaturedIdeasDesc': 'Generate more content or check your library for all ideas',
    'dashboard.noContentYet': 'No content ideas yet',
    'dashboard.completeSetup': 'Complete your setup',
    'dashboard.firstBatchGenerating': 'Your first batch is being generated! This usually takes 2-3 minutes after completing the onboarding',
    'dashboard.addInspirationSources': 'Add some inspiration sources to start generating content ideas',
    'dashboard.checkAgain': 'Check Again',
    'dashboard.addMoreSources': 'Add More Sources',
    'dashboard.getStarted': 'Get Started',
    'dashboard.topContentIdeas': 'Top 3 Content Ideas',
    'dashboard.highestViralScore': 'Highest Viral Score',
    'dashboard.used': 'Used',
    
    // Competitors
    'competitors.title': 'Competitors',
    'competitors.description': 'Track and analyze your competitors\' performance',
    'competitors.tracked': 'competitors tracked',
    'competitors.competitor': 'competitor',
    'competitors.competitors': 'competitors',
    'competitors.add': 'Add',
    'competitors.addCompetitor': 'Add Competitor',
    'competitors.enterUsername': 'Enter Instagram username or URL',
    'competitors.manageCompetitors': 'Manage Competitors',
    'competitors.addNew': 'Add new competitors or manage existing ones',
    'competitors.noCompetitors': 'No competitors added yet',
    'competitors.noCompetitorsDesc': 'Start by adding Instagram usernames of accounts you want to track and compare with your performance',
    'competitors.removeTitle': 'Remove Competitor',
    'competitors.removeDesc': 'Are you sure you want to remove @{username} from your competitors list? This action cannot be undone.',
    'competitors.remove': 'Remove',
    'competitors.overview': 'Overview',
    'competitors.growth': 'Growth',
    'competitors.manage': 'Manage',
    'competitors.performanceOverview': 'Performance Overview',
    'competitors.growthComparison': 'Growth Comparison',
    'competitors.weeklyStats': 'Weekly Stats',
    'competitors.followers': 'Followers',
    'competitors.following': 'Following',
    'competitors.posts': 'Posts',
    'competitors.performanceComparison': 'Performance Comparison',
    'competitors.comparePerformance': 'Compare your performance with all competitors',
    'competitors.youVsBest': 'You vs your best competitors in the last 7 days',
    'competitors.showMore': 'Show More Competitors',
    'competitors.followerGrowth': 'Follower growth percentage over time - normalized for better comparison',
    'competitors.postsPerWeek': 'Posts/week',
    'competitors.collectingData': 'Collecting data...',
    'competitors.fullMetrics': 'Full metrics in {days} days',
    'competitors.you': 'You',
    'competitors.chartNotReady': 'Chart will be available after collecting at least 2 days of data. Check back soon.',
    'competitors.dataCollectionInfo': 'We\'re collecting data from Instagram profiles. Full metrics will be available in a few days.',
    'competitors.trackPerformance': 'Track competitor performance',
    'competitors.dataCollectionInProgress': 'Data collection in progress',
    
    // First Content Celebration
    'firstContent.title': 'Your first content ideas are ready!',
    'firstContent.description': 'We\'ve generated {{count}} ideas from your inspiration sources.',
    'firstContent.pipelineActive': 'Your content pipeline is now active! New ideas will be generated automatically.',
    'firstContent.viewButton': 'View My Content Ideas',
    
    // Library
    'library.mainTitle': 'Content Library',
    'library.mainDescription': 'Browse and organize content ideas from your inspiration sources',
    'library.topIdeas': 'Top 3 Ideas',
    'library.favorites': 'Favorites',
    'library.noFavorites': 'No favorite ideas',
    'library.noFavoritesHint': 'Click the heart icon on an idea to add it to favorites',
    'library.allIdeas': 'All Ideas',
    'library.basedOn': 'Based on:',
    'library.viewIdea': 'View Idea',
    'library.loading': 'Loading your library...',
    'library.failedToLoad': 'Failed to load content',
    'library.tryAgain': 'Try Again',
    'library.noContent': 'No content from inspiration sources yet',
    'library.noContentDesc': 'Content ideas generated from your inspiration sources will appear here',
    'library.goToDashboard': 'Go to Dashboard',
    'library.filters': 'Filters:',
    'library.source': 'Source:',
    'library.allSources': 'All Sources',
    'library.score': 'Score:',
    'library.allScores': 'All Scores',
    'library.highScore': 'High (8+)',
    'library.mediumScore': 'Medium (5-7)',
    'library.lowScore': 'Low (0-4)',
    'library.date': 'Date:',
    'library.newestFirst': 'Newest First',
    'library.oldestFirst': 'Oldest First',
    'library.noMatches': 'No content matches your filters',
    'library.noMatchesDesc': 'Try adjusting your filter criteria to see more results',
    'library.clearAllFilters': 'Clear All Filters',
    'library.totalIdeas': 'Total Ideas',
    'library.avgScore': 'Avg Score',
    'library.itemsPerPage': 'Per page',
    
    // Favorites
    'favorites.title': 'Favorite Content',
    'favorites.description': 'Your saved content ideas',
    'favorites.backToLibrary': 'Back to Library',
    'favorites.favoriteContent': 'Favorite Content',
    'favorites.savedContent': 'Your saved content ideas for quick access',
    'favorites.filters': 'Filters:',
    'favorites.source': 'Source:',
    'favorites.allSources': 'All Sources',
    'favorites.score': 'Score:',
    'favorites.allScores': 'All Scores',
    'favorites.date': 'Date:',
    'favorites.newestFirst': 'Newest First',
    'favorites.oldestFirst': 'Oldest First',
    'favorites.showing': 'Showing {filtered} of {total} favorite ideas',
    'favorites.noFavorites': 'No favorite content yet',
    'favorites.noFavoritesDesc': 'Click the heart icon on any content idea to add it to your favorites',
    'favorites.browseContent': 'Browse Content',
    'favorites.noMatch': 'No favorites match your filters',
    'favorites.noMatchDesc': 'Try adjusting your filter criteria to see more results',
    'favorites.clearAllFilters': 'Clear All Filters',
    'favorites.favoriteIdeas': 'Favorite Ideas',
    'favorites.avgScore': 'Avg Score',
    'favorites.loadingFavorites': 'Loading your favorites...',
    'favorites.failedToLoad': 'Failed to load content',
    'favorites.highScore': 'High (8+)',
    'favorites.mediumScore': 'Medium (5-7)',
    'favorites.lowScore': 'Low (0-4)',
    // Favorites Tabs
    'favorites.tabs.all': 'All',
    'favorites.tabs.inspiration': 'Inspiration',
    'favorites.tabs.generated': 'Generated',
    
    // Inspiration
    'inspiration.title': 'Inspiration Sources',
    'inspiration.description': 'Add up to 5 profiles to scrape for content inspiration',
    'inspiration.sources': '{current}/{max} sources',
    'inspiration.nextScraping': 'Next Scheduled Scraping',
    'inspiration.weeklyRefresh': 'Weekly content refresh',
    'inspiration.scrapingSchedule': 'Scraping happens every Monday at 9:00 AM',
    'inspiration.addSource': 'Draw inspiration from around the world. Add a source:',
    'inspiration.addSourceDesc': 'Add Instagram, TikTok, or YouTube profiles to scrape for content ideas',
    'inspiration.enterUrl': 'Enter Instagram, TikTok, or YouTube URL',
    'inspiration.enterInstagramUrl': 'Enter Instagram username or URL',
    'inspiration.addButton': 'Add Source',
    'inspiration.maxReached': 'You\'ve reached the maximum number of inspiration sources.',
    'inspiration.postsScraped': 'Posts Scraped',
    'inspiration.posts': 'Posts',
    'inspiration.lastScraped': 'Last Scraped',
    'inspiration.never': 'Never',
    'inspiration.noSources': 'No inspiration sources added yet',
    'inspiration.noSourcesDesc': 'Start by adding social media profiles you want to scrape for content inspiration',
    'inspiration.removeSource': 'Remove Inspiration Source',
    'inspiration.removeSourceDesc': 'Are you sure you want to remove @{username}? This will stop future content scraping from this source, but your existing content will be preserved in your Library.',
    'inspiration.scrapingContent': 'Scraping content...',
    'inspiration.completedCount': '{completed}/{total} completed',
    'inspiration.processing': 'Processing @{username}...',
    'inspiration.adding': 'Adding...',
    'inspiration.addSourceButton': 'Add Source',
    'inspiration.maxSourcesReached': 'You\'ve reached the maximum number of inspiration sources.',
    'inspiration.loadingSources': 'Loading inspiration sources...',
    'inspiration.invalidUrl': 'Invalid URL',
    'inspiration.invalidUrlDesc': 'Please enter a valid Instagram, TikTok, or YouTube URL',
    'inspiration.sourceAdded': 'Source added!',
    'inspiration.sourceAddedDesc': 'Content scraping will begin shortly. Check back in a few minutes for new ideas.',
    'inspiration.failedToAdd': 'Failed to add source',
    'inspiration.sourceRemoved': 'Source removed',
    'inspiration.sourceRemovedDesc': '@{username} removed. Your existing content from this source has been preserved.',
    'inspiration.failedToRemove': 'Failed to remove source',
    
    // Analytics
    'analytics.title': 'Analytics',
    'analytics.description': 'Track your Instagram growth and content performance',
    'analytics.followers': 'Followers',
    'analytics.following': 'Following',
    'analytics.posts': 'Posts',
    'analytics.thisWeek': 'this week',
    'analytics.thisMonth': 'this month',
    'analytics.weeklyGrowth': 'Weekly growth tracking',
    'analytics.followingActivity': 'Following activity',
    'analytics.contentActivity': 'Content activity',
    'analytics.monthlyGrowth': 'Monthly Growth',
    'analytics.weeklyPerformance': 'Weekly performance',
    'analytics.monthlyPerformance': 'Monthly performance',
    'analytics.last7Days': 'Last 7 days trend',
    'analytics.last30Days': 'Last 30 days trend',
    'analytics.weeklyGoal': 'Weekly Goal 🎯',
    'analytics.monthlyGoal': 'Monthly Goal 🎯',
    'analytics.getIdeas': 'Get Ideas',
    'analytics.overview': 'Overview',
    'analytics.growthAnalysis': 'Growth Analysis',
    'analytics.contentPerformance': 'Content Performance',
    'analytics.noData': 'No Analytics Data Available',
    'analytics.noDataDesc': 'Connect your Instagram profile and wait for data collection to begin seeing your analytics.',
    'analytics.dataCollection': 'Data collection in progress',
    'analytics.chartTitle': 'Growth Overview',
    'analytics.chartDescription': 'Green bars show follower gains, red bars show losses over the selected period',
    'analytics.lastWeek': 'Last 7 days',
    'analytics.lastMonth': 'Last 30 days',
    'analytics.dailyChange': 'Daily Change',
    'analytics.followerChange': 'Follower Change',
    'analytics.followerGains': 'Follower Gains',
    'analytics.followerLosses': 'Follower Losses',
    'analytics.bestGrowthDay': 'Best Growth Day',
    'analytics.totalWeeklyChange': 'Total Weekly Change',
    'analytics.totalMonthlyChange': 'Total Monthly Change',
    'analytics.growthDays': 'Growth Days',
    'analytics.followersGained': '+{count} followers gained',
    'analytics.netFollowerChange': 'Net follower change this week',
    'analytics.netFollowerChangeMonth': 'Net follower change this month',
    'analytics.outOfDays': 'Out of {count} days tracked',
    'analytics.collecting': 'Collecting...',
    'analytics.needMoreData': 'Need more data to analyze',
    'analytics.noGainsThisWeek': 'No gains this week',
    'analytics.noGainsThisMonth': 'No gains this month',
    'analytics.contentIdeas': 'Content Ideas',
    'analytics.inspirationSources': 'Inspiration Sources',
    'analytics.viralScore': 'Viral Score',
    'analytics.generatedIdeas': 'Generated ideas',
    'analytics.activeSources': 'Active sources',
    'analytics.averageQuality': 'Average quality',
    'analytics.aiPowered': 'AI-powered',
    'analytics.trending': 'Trending',
    'analytics.highQuality': 'High Quality',
    'analytics.mediumQuality': 'Medium Quality',
    'analytics.lowQuality': 'Low Quality',
    'analytics.totalIdeas': 'Content Ideas',
    'analytics.aiGeneratedContent': 'AI Generated',
    'analytics.averageContentScore': 'Average Quality Score',
    'analytics.basedOnAIIdeas': 'Based on your ideas',
    'analytics.scoreInterpretation': 'Score Interpretation',
    'analytics.understandYourScore': 'Understand your inspiration sources quality',
    'analytics.excellentContent': 'Excellent inspiration sources',
    'analytics.goodContent': 'Good inspiration sources',
    'analytics.needsImprovement': 'Needs improvement',
    'analytics.noContentYet': 'No content yet',
    'analytics.startGenerating': 'Start generating content ideas using our AI',
    'analytics.generateFirstIdea': 'Generate first idea',
    
    // Idea Generator
    'ideaGenerator.title': 'Scenario Generator',
    'ideaGenerator.description': 'Generate social media scenarios on a chosen topic',
    'ideaGenerator.topicLabel': 'Scenario topic',
    'ideaGenerator.topicPlaceholder': 'Enter a topic for which you want to generate social media scenarios...',
    'ideaGenerator.topicHint': 'Describe the topic as detailed as possible to get better results. Specify your audience gender if you target content mainly to women, for example',
    'ideaGenerator.generateButton': 'Generate ideas',
    'ideaGenerator.generating': 'Generating...',
    
    // Settings
    'settings.title': 'Settings',
    'settings.description': 'Manage your account settings and preferences',
    'settings.industrySettings': 'Industry Settings',
    'settings.updateIndustry': 'Update your industry to get more relevant content suggestions',
    'settings.currentIndustry': 'Current Industry',
    'settings.accountInfo': 'Account Information',
    'settings.viewAccountDetails': 'View your current account details',
    'settings.securitySettings': 'Security Settings',
    'settings.emailAddress': 'Email Address',
    'settings.instagramUsername': 'Instagram Username',
    'settings.changeEmail': 'Change Email Address',
    'settings.changeEmailDesc': 'Update your email address. You\'ll need to confirm the new email.',
    'settings.newEmail': 'New Email Address',
    'settings.updateEmail': 'Update Email',
    'settings.changePassword': 'Change Password',
    'settings.changePasswordDesc': 'Update your account password. You\'ll need to enter your current password.',
    'settings.currentPassword': 'Current Password',
    'settings.newPassword': 'New Password',
    'settings.confirmPassword': 'Confirm New Password',
    'settings.updatePassword': 'Update Password',
    'settings.saving': 'Saving...',
    'settings.updating': 'Updating...',
    'settings.notAuthenticated': 'User not authenticated',
    'settings.selectIndustry': 'Please select an industry',
    'settings.industryUpdated': 'Industry updated',
    'settings.industryUpdatedDesc': 'Your industry has been successfully updated.',
    'settings.errorUpdatingIndustry': 'Error updating industry',
    'settings.industryNotSet': 'Industry not yet selected',
    'settings.editIndustry': 'Edit industry',
    'settings.legacyCategoryNotice': "We've refreshed our category list. Pick again so Viraluj generates better-targeted content.",
    'settings.enterNewEmail': 'Please enter a new email address',
    'settings.emailUpdateInitiated': 'Email Update Initiated',
    'settings.emailUpdateInitiatedDesc': 'Check your new email address for a confirmation link to complete the change.',
    'settings.errorUpdatingEmail': 'Error updating email',
    'settings.fillPasswordFields': 'Please fill in all password fields',
    'settings.passwordsDoNotMatch': 'New passwords do not match',
    'settings.passwordTooShort': 'New password must be at least 6 characters long',
    'settings.currentPasswordIncorrect': 'Current password is incorrect',
    'settings.passwordUpdated': 'Password updated',
    'settings.passwordUpdatedDesc': 'Your password has been successfully updated.',
    'settings.errorUpdatingPassword': 'Error updating password',
    'settings.selectIndustryPlaceholder': 'Select your industry',
    'settings.notSelected': 'Not selected',
    'settings.notProvided': 'Not provided',
    'settings.industries.fashion': 'Fashion & Lifestyle',
    'settings.industries.food': 'Food & Beverage',
    'settings.industries.business': 'Business & Finance',
    'settings.industries.gaming': 'Gaming & Tech',
    'settings.industries.automotive': 'Automotive',
    'settings.industries.retail': 'Retail',
    'settings.industries.realestate': 'Real Estate',
    'settings.industries.other': 'Other',
    'settings.instagram.title': 'Instagram Profile',
    'settings.instagram.description': 'Connect your Instagram profile',
    'settings.instagram.usernameLabel': 'Instagram Username',
    'settings.instagram.usernamePlaceholder': 'your_username',
    'settings.instagram.locked': 'This field is locked for data integrity.',
    
    // Welcome Dialog
    'welcome.title': 'Welcome! 🎉',
    'welcome.titleWithName': 'Welcome, {{name}}! 🎉',
    'welcome.subtitle': 'Get to know your new viral Instagram content creation platform',
    'welcome.feature1Title': '1. Analyze Competitors',
    'welcome.feature1Desc': 'We track your competitors and analyze their most successful content so you know what works in your niche.',
    'welcome.feature2Title': '2. Generate AI Ideas',
    'welcome.feature2Desc': 'Our AI creates personalized content ideas based on viral patterns and your industry.',
    'welcome.feature3Title': '3. Post & Grow',
    'welcome.feature3Desc': 'Use ready-made scripts, track your growth, and outperform competitors with data-driven insights.',
    'welcome.tipTitle': '💡 Quick Start',
    'welcome.tipDesc': 'Start by adding inspiration sources in the Inspiration tab, and we\'ll automatically generate your first content ideas!',
    'welcome.exploreDashboard': 'Explore Dashboard',
    'welcome.getStarted': 'Add Inspiration Sources',
    'settings.instagram.usernameHint': 'Enter username or paste Instagram profile link',
    'settings.instagram.saveButton': 'Save Instagram Username',
    'settings.instagram.notAuthenticated': 'You must be logged in to save your Instagram username.',
    'settings.instagram.enterUsername': 'Please enter an Instagram username.',
    'settings.instagram.cannotChange': 'Cannot Change Username',
    'settings.instagram.cannotChangeDesc': 'Your Instagram username is already set and cannot be changed. Please contact support if you need to update it.',
    'settings.instagram.saved': 'Instagram Username Saved',
    'settings.instagram.savedDesc': 'Your Instagram username has been saved successfully.',
    'settings.enterNewEmailPlaceholder': 'Enter new email address',
    'settings.enterCurrentPasswordPlaceholder': 'Enter current password',
    'settings.enterNewPasswordPlaceholder': 'Enter new password',
    'settings.confirmPasswordPlaceholder': 'Confirm new password',
    
    // Days of week
    'day.mon': 'Mon',
    'day.tue': 'Tue',
    'day.wed': 'Wed',
    'day.thu': 'Thu',
    'day.fri': 'Fri',
    'day.sat': 'Sat',
    'day.sun': 'Sun',
    
    // Library
    'library.title': 'Content Library',
    'library.description': 'Browse and organize content ideas from your inspiration sources',
    'library.seeMore': 'See More',
    'library.pagination.previous': 'Previous',
    'library.pagination.next': 'Next',
    'library.pagination.pageInfo': 'Page {{current}} of {{total}}',
    'library.view': 'View',
    'library.viewCork': 'Board',
    'library.viewList': 'List',
    'library.tabs.all': 'All',
    'library.tabs.best': 'Best',
    'library.tabs.used': 'Used',
    'library.tabs.generated': 'Original',
    'library.tabs.trash': 'Trash',
    
    // Header
    'header.notifications': 'Notifications',
    'header.markAllRead': 'Mark all read',
    'header.noNotifications': 'No notifications yet',
    'header.settings': 'Settings',
    'header.logout': 'Log out',
    
    // Content Pipeline
    'contentPipeline.title': 'Content Pipeline',
    'contentPipeline.lastUpdated': 'Last updated: {time}',
    'contentPipeline.sources': 'Sources:',
    'contentPipeline.of': 'of',
    'contentPipeline.used': 'used',
    'contentPipeline.addMoreSources': 'Add More Sources',
    'contentPipeline.limitReached': 'Limit Reached',
    'contentPipeline.addInstagramSource': 'Add Instagram Source',
    'contentPipeline.limitReachedDesc': 'You\'ve reached the 5 source limit. Remove a source first to add a new one.',
    'contentPipeline.slotsRemaining': '{count} slots remaining',
    'contentPipeline.instagramProfileUrl': 'Instagram Profile URL',
    'contentPipeline.usernamePlaceholder': '@username or https://instagram.com/username',
    'contentPipeline.adding': 'Adding...',
    'contentPipeline.addSource': 'Add Source',
    'contentPipeline.yourInspirationSources': 'Your Inspiration Sources ({current} of {max})',
    'contentPipeline.limitReachedBadge': 'Limit reached',
    'contentPipeline.removeSourceTip': '💡 Remove a source to add a new one',
    'contentPipeline.generatingContent': '🔄 Generating Content',
    'contentPipeline.analyzingSources': 'We\'re analyzing your {count} inspiration sources...',
    'contentPipeline.usuallyTakes': 'This usually takes 2-3 minutes.',
    'contentPipeline.loadingPipeline': 'Loading pipeline status...',
    'contentPipeline.justNow': 'Just now',
    'contentPipeline.minutesAgo': '{count} minutes ago',
    'contentPipeline.hoursAgo': '{count} hours ago',
    'contentPipeline.daysAgo': '{count} days ago',
    'contentPipeline.pleaseEnterUrl': 'Please enter an Instagram URL',
    
    // Onboarding Generation Status
    'onboardingGeneration.title': '🔄 Generating Your First Content Ideas',
    'onboardingGeneration.analyzingSources': 'We\'re analyzing your {count} inspiration sources...',
    'onboardingGeneration.usuallyTakes': 'This usually takes 2-3 minutes.',
    'onboardingGeneration.analyzingPatterns': 'Analyzing viral content patterns...',
    'onboardingGeneration.autoRefresh': '💡 We\'ll auto-refresh every 30 seconds to check for new content',
    
    // Product Tour
    'tour.welcome.title': 'Welcome to Viraluj.pl!',
    'tour.welcome.description': 'Discover key platform features in 1 minute. We\'ll guide you step by step through all possibilities.',
    'tour.profileStats.title': '📊 Your Statistics',
    'tour.profileStats.description': 'Track your Instagram profile growth - followers, posts, and weekly goals.',
    'tour.competitors.title': '🎯 Competitor Analysis',
    'tour.competitors.description': 'See what works for your competitors. We analyze their best content so you can stay ahead.',
    'tour.competitors.navTitle': '📈 Full Analysis',
    'tour.competitors.navDescription': 'Detailed performance comparisons and competitor strategies. Everything in one place.',
    'tour.featuredIdeas.title': '💡 Top 3 Ideas',
    'tour.featuredIdeas.description': 'Best ideas selected based on viral potential. Click to see full details!',
    'tour.library.title': '📚 Ideas Library',
    'tour.library.description': 'All generated content ideas in one place. Your personal inspiration treasury!',
    'tour.favorites.title': '❤️ Favorites',
    'tour.favorites.description': 'Click the heart icon to save the best ideas. Easy to return to them!',
    'tour.favoritesPage.title': '⭐ Your Favorites',
    'tour.favoritesPage.description': 'Saved ideas and ability to mark as "used" after publishing. Organize your work!',
    'tour.inspiration.title': '✨ Inspiration Sources',
    'tour.inspiration.description': 'Add Instagram profiles that inspire you. We automatically analyze their best content.',
    'tour.analytics.title': '📊 Analytics',
    'tour.analytics.description': 'Track progress and content effectiveness. See exactly what works in your strategy!',
    'tour.scenarioCreator.title': '✍️ Scenario Creator',
    'tour.scenarioCreator.description': 'Create your first scenario! Enter a topic and we\'ll generate a ready-to-record script.',
    'tour.scenarioCreator.cta': '👉 Click "Finish" to go to the creator and make your first scenario!',
    'tour.buttons.back': 'Back',
    'tour.buttons.close': 'Close',
    'tour.buttons.finish': 'Create scenario →',
    'tour.buttons.next': 'Next',
    'tour.buttons.skip': 'Skip',
    
    // Settings Product Tour
    'settings.productTour.title': 'Platform Tour',
    'settings.productTour.description': 'Restart the interactive tour that shows you the key features of the platform.',
    'settings.productTour.restart': 'Start Tour',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('language');
    return (stored as Language) || 'pl'; // Polish as default
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string, replacements?: Record<string, string>): string => {
    // Resolve nested keys like "Landing.Hero.title" while keeping backward compatibility with flat keys
    const resolveNested = (obj: Record<string, unknown>, path: string): unknown =>
      path.split('.').reduce<unknown>((acc: unknown, part: string) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined), obj);

    const nested = resolveNested(translations[language], key);
    let translation = typeof nested === 'string' ? nested : (translations[language][key] as string) || key;
    
    if (replacements) {
      Object.entries(replacements).forEach(([placeholder, value]) => {
        translation = translation.replace(`{${placeholder}}`, value);
      });
    }
    
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
